import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaTrash, FaPlus, FaMinus, FaShoppingBag, FaMotorcycle, FaStore, FaCalendarPlus, FaPen } from 'react-icons/fa';
import { useCart } from '../../context/CartContext';
import { useStoreStatus } from '../../context/StoreStatusContext';
import { useSubscriptionCart } from '../../context/SubscriptionCartContext';
import { KITCHEN_CLOSED_CHECKOUT_MESSAGE } from '../../utils/storeStatus';
import { buildAuthRequiredRedirectUrl, readStoredAuth } from '../Account/profileFormUtils';
import CartCouponSection from './CartCouponSection';
import CartDeliveryAddressPicker from './CartDeliveryAddressPicker';
import { computeOrderGrandTotal } from './cartCouponUtils';
import { useCartCoupons } from './useCartCoupons';
import { useCartCharges, useCartDeliveryDistance } from './useCartCharges';
import {
  DeferredChargeLabel,
  DeferredChargeValue,
  OrderSummaryDeferredFootnote,
} from '../../components/OrderSummaryDeferredNote/OrderSummaryDeferredNote';
import CartLineAddOns from '../../components/CartLineAddOns';
import CartCustomiseController, {
  cartLineCanCustomise,
  useCartCustomise,
} from '../../components/CartCustomiseController';
import {
  getCartLineHostUnitPrice,
  getCartLineTotal,
  normalizeCartLineAddOns,
  resolveCartLineKey,
} from '../OurMenu/cartAddOnUtils';
import './Cart.css';

const Cart = () => {
  const { cartItems, removeFromCart, updateQty, clearCart, cartTotal } = useCart();
  const { addToSubscriptionCart } = useSubscriptionCart();
  const { isKitchenOpen } = useStoreStatus();
  const navigate = useNavigate();
  const [fulfillmentType, setFulfillmentType] = useState('delivery');
  const isSignedIn = Boolean(readStoredAuth().token);
  const {
    editingLine,
    openCustomise,
    closeCustomise,
    menuItems: customiseMenuItems,
    menuStatus: customiseMenuStatus,
  } = useCartCustomise(cartItems);

  const coupon = useCartCoupons({ cartTotal });
  const {
    distanceKm,
    selectedAddress,
    selectedId,
    addresses,
    status: addressStatus,
    selectAddress,
    reload: reloadAddresses,
  } = useCartDeliveryDistance({
    enabled: fulfillmentType === 'delivery',
  });

  const charges = useCartCharges({
    cartItems,
    cartTotal,
    fulfillmentType,
    appliedCoupon: coupon.appliedCoupon,
    distanceKm: fulfillmentType === 'pickup' ? 0 : distanceKm,
  });

  const {
    packagingFee,
    delivery,
    gstTax,
    gstPercent,
    discount,
    deferDeliveryAndTax,
    baseDelivery,
    status: chargesStatus,
    error: chargesError,
    itemsAvailable,
    hasUnavailableItems,
    isCartItemUnavailable,
    chargeSource,
  } = charges;

  const moveItemToSubscription = (item) => {
    addToSubscriptionCart(item, { quantity: item.quantity });
    removeFromCart(resolveCartLineKey(item));
  };

  const moveAllToSubscription = () => {
    cartItems.forEach((item) => {
      addToSubscriptionCart(item, { quantity: item.quantity, showToast: false });
    });
    clearCart();
    navigate('/subscription-cart');
  };

  const grandTotal = computeOrderGrandTotal({
    cartTotal,
    discount,
    packagingFee,
    delivery,
    gstTax,
    deferDeliveryAndTax,
  });

  const checkoutDisabled = chargesStatus === 'loading' || hasUnavailableItems;
  const checkoutBlockedByKitchen = !isKitchenOpen;

  const goToCheckout = () => {
    if (checkoutDisabled || checkoutBlockedByKitchen) return;
    if (!readStoredAuth().token) {
      navigate(buildAuthRequiredRedirectUrl('/checkout'));
      return;
    }
    navigate('/checkout', {
      state: {
        discount,
        totalDiscount: discount,
        appliedCoupon: coupon.appliedCoupon,
        couponCode: coupon.appliedCoupon?.code ?? null,
        delivery,
        baseDelivery,
        grandTotal,
        packagingFee,
        gstTax,
        gstPercent,
        fulfillmentType,
        distanceKm: fulfillmentType === 'pickup' ? 0 : distanceKm,
      },
    });
  };

  if (cartItems.length === 0) {
    return (
      <div className="cart-page">
        <div className="page-header">
          <div className="page-header-content">
            <div className="container">
              <h1>YOUR CART</h1>
              <div className="breadcrumb">
                <Link to="/">Home</Link>
                <span>/</span>
                <span>Cart</span>
              </div>
            </div>
          </div>
        </div>
        <section className="cart-empty">
          <div className="container">
            <FaShoppingBag className="cart-empty__icon" />
            <h2>Your cart is empty</h2>
            <p>Looks like you haven't added anything yet. Explore our menu!</p>
            <Link to="/our-menu" className="btn-primary">
              BROWSE MENU
            </Link>
            <p className="cart-empty__plan-hint">
              Building a meal plan?{' '}
              <Link to="/subscription-cart">Open subscription cart</Link>
              {' '}or{' '}
              <Link to="/add-subscription">create a subscription</Link>.
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="container">
            <h1>YOUR CART</h1>
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Cart</span>
            </div>
          </div>
        </div>
      </div>

      <section className="cart-section">
        <div className="container">
          <div className="cart-layout">
            <div className="cart-items">
              <div className="cart-items__header">
                <h2>
                  Order Items{' '}
                  <span>
                    ({cartItems.length} {cartItems.length === 1 ? 'item' : 'items'})
                  </span>
                </h2>
                <div className="cart-items__header-actions">
                  <button
                    type="button"
                    className="cart-move-plan-btn"
                    onClick={moveAllToSubscription}
                  >
                    <FaCalendarPlus aria-hidden /> Move all to plan
                  </button>
                  <button className="cart-clear-btn" onClick={clearCart}>
                    Clear All
                  </button>
                </div>
              </div>

              <div className="cart-items__alerts">
                {hasUnavailableItems && (
                  <p className="cart-charges-alert" role="alert">
                    One or more meals are unavailable today. Use <strong>Remove item</strong> on
                    those lines, then proceed to checkout.
                  </p>
                )}

                {!hasUnavailableItems && !itemsAvailable && chargesStatus === 'ready' && (
                  <p className="cart-charges-alert" role="alert">
                    Some items in your cart may be unavailable. Charges below are estimates until
                    stock is confirmed at checkout.
                  </p>
                )}

                {chargesError && (
                  <p className="cart-charges-alert cart-charges-alert--warn" role="alert">
                    {chargesError} Showing estimated packaging; delivery and GST at checkout.
                  </p>
                )}
              </div>

              {cartItems.map((item) => {
                const lineKey = resolveCartLineKey(item);
                const unavailable =
                  chargesStatus === 'ready' && isCartItemUnavailable(item);
                const canCustomise =
                  !unavailable &&
                  cartLineCanCustomise(item, customiseMenuItems, customiseMenuStatus);
                return (
                <div
                  key={lineKey}
                  className={`cart-item${unavailable ? ' cart-item--unavailable' : ''}`}
                >
                  <div className="cart-item__main">
                    <div className="cart-item__img">
                      <img src={item.image} alt={item.name} />
                    </div>
                    <div className="cart-item__body">
                      <div className="cart-item__top">
                        <div className="cart-item__info">
                          {String(item.category || '').trim() &&
                          String(item.category || '').trim().toLowerCase() !== 'reorder' ? (
                            <span className="cart-item__category">{item.category}</span>
                          ) : null}
                          <h3 className="cart-item__name">{item.name}</h3>
                          {item.variantLabel ? (
                            <span className="cart-item__variant">{item.variantLabel}</span>
                          ) : null}
                          {!unavailable ? (
                            <span className="cart-item__host-meta">
                              {item.quantity > 1
                                ? `${item.quantity} plates · ₹${getCartLineHostUnitPrice(item).toFixed(2)} each`
                                : `₹${getCartLineHostUnitPrice(item).toFixed(2)}`}
                            </span>
                          ) : null}
                          {!unavailable &&
                          normalizeCartLineAddOns(item.addOns).length > 0 ? (
                            <CartLineAddOns addOns={item.addOns} />
                          ) : null}
                        </div>
                        <div className="cart-item__controls">
                          {unavailable ? (
                            <button
                              type="button"
                              className="cart-item__remove-unavailable"
                              onClick={() => removeFromCart(lineKey)}
                            >
                              <FaTrash aria-hidden /> Remove item
                            </button>
                          ) : (
                            <>
                              <div className="qty-stepper">
                                <button
                                  type="button"
                                  className="qty-btn"
                                  onClick={() => updateQty(lineKey, item.quantity - 1)}
                                  aria-label="Decrease quantity"
                                >
                                  <FaMinus />
                                </button>
                                <span className="qty-value">{item.quantity}</span>
                                <button
                                  type="button"
                                  className="qty-btn"
                                  onClick={() => updateQty(lineKey, item.quantity + 1)}
                                  aria-label="Increase quantity"
                                >
                                  <FaPlus />
                                </button>
                              </div>
                              <span className="cart-item__line-total">
                                ₹{getCartLineTotal(item).toFixed(2)}
                              </span>
                              <button
                                type="button"
                                className="cart-item__remove"
                                onClick={() => removeFromCart(lineKey)}
                                aria-label="Remove item"
                              >
                                <FaTrash />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {!unavailable ? (
                        <div className="cart-item__actions">
                          {canCustomise ? (
                            <button
                              type="button"
                              className="cart-item__action cart-item__action--edit"
                              onClick={() => openCustomise(item)}
                            >
                              <FaPen aria-hidden /> Edit
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="cart-item__action"
                            onClick={() => moveItemToSubscription(item)}
                          >
                            <FaCalendarPlus aria-hidden /> Add to plan
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  {unavailable ? (
                    <div className="cart-item__unavailable-banner" role="alert">
                      <p>
                        <strong>Unavailable for today&apos;s menu</strong>
                        <span>Remove this item from your cart to continue.</span>
                      </p>
                    </div>
                  ) : null}
                </div>
              );
              })}

              <div className="cart-continue">
                <Link to="/our-menu" className="cart-continue__link">
                  ← Browse menu to add more
                </Link>
              </div>
            </div>

            <div className="cart-summary">
              <h2 className="cart-summary__title">Order Summary</h2>

              <div className="cart-summary-setup">
                <CartCouponSection
                  cartTotal={cartTotal}
                  coupons={coupon.coupons}
                  appliedCoupon={coupon.appliedCoupon}
                  status={coupon.status}
                  error={coupon.error}
                  codeInput={coupon.codeInput}
                  setCodeInput={coupon.setCodeInput}
                  codeError={coupon.codeError}
                  setCodeError={coupon.setCodeError}
                  onBrowseApply={coupon.applyCoupon}
                  onApplyCode={coupon.handleApplyCodeInput}
                  onRemove={coupon.removeCoupon}
                  onRetry={coupon.loadCoupons}
                />

                <div className="cart-summary-setup__block cart-summary-panel cart-summary-panel--fulfillment">
                  <p className="cart-summary-setup__heading">How would you like your order?</p>
                  <div className="cart-fulfillment">
                    <div
                      className="cart-fulfillment__options"
                      role="group"
                      aria-label="Order type"
                    >
                      <button
                        type="button"
                        className={`cart-fulfillment__btn${fulfillmentType === 'delivery' ? ' cart-fulfillment__btn--active' : ''}`}
                        onClick={() => setFulfillmentType('delivery')}
                        aria-pressed={fulfillmentType === 'delivery'}
                      >
                        <span className="cart-fulfillment__head">
                          <FaMotorcycle aria-hidden /> Delivery
                        </span>
                        <small>Delivered to your address</small>
                      </button>
                      <button
                        type="button"
                        className={`cart-fulfillment__btn${fulfillmentType === 'pickup' ? ' cart-fulfillment__btn--active' : ''}`}
                        onClick={() => setFulfillmentType('pickup')}
                        aria-pressed={fulfillmentType === 'pickup'}
                      >
                        <span className="cart-fulfillment__head">
                          <FaStore aria-hidden /> Pickup
                        </span>
                        <small>No delivery charges</small>
                      </button>
                    </div>
                  </div>
                </div>

                {fulfillmentType === 'delivery' ? (
                  <CartDeliveryAddressPicker
                    status={addressStatus}
                    addresses={addresses}
                    selectedId={selectedId}
                    selectedAddress={selectedAddress}
                    onSelect={selectAddress}
                    onReload={reloadAddresses}
                  />
                ) : null}
              </div>

              <div className="cart-breakdown">
                <div className="cart-breakdown__row cart-breakdown__row--fulfillment">
                  <span>Order Type</span>
                  <span className="cart-order-type">
                    {fulfillmentType === 'pickup' ? <FaStore /> : <FaMotorcycle />}
                    {fulfillmentType === 'pickup' ? 'Pickup' : 'Delivery'}
                  </span>
                </div>
                <div className="cart-breakdown__row">
                  <span>Subtotal</span>
                  <span>₹{cartTotal.toFixed(2)}</span>
                </div>
                {discount > 0 && coupon.appliedCoupon && (
                  <div className="cart-breakdown__row cart-breakdown__row--discount">
                    <span>Discount ({coupon.appliedCoupon.code})</span>
                    <span>−₹{discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="cart-breakdown__row">
                  <span>Packaging fee</span>
                  <span>
                    {chargesStatus === 'loading' ? '…' : `₹${packagingFee.toFixed(2)}`}
                  </span>
                </div>
                {fulfillmentType !== 'pickup' ? (
                  <div
                    className={`cart-breakdown__row${deferDeliveryAndTax ? ' cart-breakdown__row--pending' : ''}`}
                  >
                    <span>
                      {deferDeliveryAndTax ? (
                        <DeferredChargeLabel>Delivery</DeferredChargeLabel>
                      ) : (
                        'Delivery'
                      )}
                    </span>
                    <span>
                      {deferDeliveryAndTax ? (
                        <DeferredChargeValue />
                      ) : chargesStatus === 'loading' ? (
                        '…'
                      ) : delivery === 0 ? (
                        <span className="cart-free-delivery">FREE</span>
                      ) : (
                        `₹${delivery.toFixed(2)}`
                      )}
                    </span>
                  </div>
                ) : null}
                <div
                  className={`cart-breakdown__row${deferDeliveryAndTax ? ' cart-breakdown__row--pending' : ''}`}
                >
                  <span>
                    {deferDeliveryAndTax ? (
                      <DeferredChargeLabel>GST / Tax</DeferredChargeLabel>
                    ) : (
                      'GST / Tax'
                    )}
                  </span>
                  <span>
                    {deferDeliveryAndTax ? (
                      <DeferredChargeValue />
                    ) : chargesStatus === 'loading' ? (
                      '…'
                    ) : (
                      `₹${gstTax.toFixed(2)}`
                    )}
                  </span>
                </div>
                {deferDeliveryAndTax && fulfillmentType === 'delivery' && (
                  <OrderSummaryDeferredFootnote>
                    Choose a delivery address with map location above to calculate delivery and
                    GST.
                  </OrderSummaryDeferredFootnote>
                )}
                {chargeSource === 'estimate' && coupon.status === 'guest' && (
                  <p className="cart-charges-hint">
                    Sign in to refresh packaging, delivery, and tax from your saved address.
                  </p>
                )}
                <div className="cart-breakdown__divider" />
                <div className="cart-breakdown__row cart-breakdown__row--total">
                  <span>{deferDeliveryAndTax ? 'Total (estimated)' : 'Total'}</span>
                  <span>
                    {chargesStatus === 'loading' ? '…' : `₹${grandTotal.toFixed(2)}`}
                  </span>
                </div>
              </div>

              {checkoutBlockedByKitchen ? (
                <p className="cart-checkout-blocked" role="status">
                  {KITCHEN_CLOSED_CHECKOUT_MESSAGE}{' '}
                  <Link to="/subscription-cart">Move items to a subscription</Link>.
                </p>
              ) : hasUnavailableItems ? (
                <p className="cart-checkout-blocked" role="status">
                  Remove unavailable items from your cart to proceed to checkout.
                </p>
              ) : !isSignedIn ? (
                <p className="cart-checkout-blocked" role="status">
                  <Link to={buildAuthRequiredRedirectUrl('/checkout')}>Sign in</Link> to proceed to
                  checkout and complete your order.
                </p>
              ) : null}

              <button
                type="button"
                className={`cart-checkout-btn${checkoutBlockedByKitchen ? ' cart-checkout-btn--kitchen-closed' : ''}`}
                disabled={checkoutDisabled || checkoutBlockedByKitchen}
                aria-disabled={checkoutDisabled || checkoutBlockedByKitchen}
                onClick={goToCheckout}
              >
                PROCEED TO CHECKOUT
              </button>
              <button
                type="button"
                className="cart-move-plan-btn cart-move-plan-btn--block"
                onClick={moveAllToSubscription}
              >
                <FaCalendarPlus aria-hidden /> Move all to subscription
              </button>

              <div className="cart-summary__secure">🔒 Secure checkout · 100% fresh</div>
            </div>
          </div>
        </div>
      </section>

      <div className="cart-mobile-bar">
        <div className="cart-mobile-bar__total">
          <span>Total</span>
          <strong>{chargesStatus === 'loading' ? '…' : `₹${grandTotal.toFixed(2)}`}</strong>
        </div>
        <button
          type="button"
          className={`cart-mobile-bar__pay${checkoutBlockedByKitchen ? ' cart-mobile-bar__pay--kitchen-closed' : ''}`}
          disabled={checkoutDisabled || checkoutBlockedByKitchen}
          onClick={goToCheckout}
        >
          Checkout
        </button>
      </div>

      <CartCustomiseController
        open={Boolean(editingLine)}
        cartLine={editingLine}
        onClose={closeCustomise}
        menuItems={customiseMenuItems}
        menuStatus={customiseMenuStatus}
      />
    </div>
  );
};

export default Cart;
